<template>
  <b-nav-item-dropdown
    right
    class="app-notification-bell"
    toggle-class="app-notification-bell-toggle"
    menu-class="app-notification-bell-menu"
    @show="onOpen"
  >
    <template #button-content>
      <span class="app-notification-bell-icon">
        <app-icon icon="bell-fill" scale="1.15" />
        <span v-if="unreadCount > 0" class="app-notification-bell-badge" aria-hidden="true">
          {{ unreadCountLabel }}
        </span>
      </span>
    </template>

    <div class="app-notification-bell-panel-header">
      <div class="app-notification-bell-title-row">
        <div class="app-notification-bell-title">
          {{ $t("notifications") }}
          <span v-if="unreadCount > 0" class="app-notification-bell-count">{{ unreadCountLabel }}</span>
        </div>
        <button
          v-if="unreadCount > 0"
          type="button"
          class="app-notification-bell-mark-all"
          @click="onMarkAllRead"
        >
          <app-icon icon="check-all" />
          {{ $t("notifications-mark-all-read") }}
        </button>
      </div>

      <div class="app-notification-bell-filter" role="group" :aria-label="$t('notifications').toString()">
        <button
          type="button"
          class="app-notification-bell-filter-button"
          :class="{ 'app-notification-bell-filter-button-active': !showReadNotifications }"
          @click="onShowUnread"
        >
          {{ $t("notifications-show-unread-only") }}
        </button>
        <button
          type="button"
          class="app-notification-bell-filter-button"
          :class="{ 'app-notification-bell-filter-button-active': showReadNotifications }"
          @click="onShowAll"
        >
          {{ $t("notifications-show-read") }}
        </button>
      </div>
    </div>

    <div class="app-notification-bell-list">
      <div v-if="loading" class="app-notification-bell-loading">
        <b-spinner small />
      </div>

      <div v-else-if="notifications.length === 0" class="app-notification-bell-empty">
        <span class="app-notification-bell-empty-icon"><app-icon icon="inbox" /></span>
        <span>{{ showReadNotifications ? $t("notifications-empty") : $t("notifications-empty-unread") }}</span>
      </div>

      <template v-else>
        <section
          v-for="group in notificationGroups"
          :key="group.key"
          class="app-notification-bell-group"
        >
          <div
            class="app-notification-bell-group-header"
            :class="{ 'app-notification-bell-group-header-collapsed': isNotificationGroupCollapsed(group.key) }"
          >
            <button
              type="button"
              class="app-notification-bell-group-toggle"
              :aria-expanded="(!isNotificationGroupCollapsed(group.key)).toString()"
              @click.stop="onToggleNotificationGroup(group.key)"
            >
              <span class="app-notification-bell-group-icon"><app-icon icon="folder2-open" /></span>
              <span class="app-notification-bell-group-name">{{ group.label }}</span>
              <app-icon
                :icon="isNotificationGroupCollapsed(group.key) ? 'chevron-down' : 'chevron-up'"
                class="app-notification-bell-group-toggle-icon"
              />
            </button>
            <span v-if="group.unreadCount > 0" class="app-notification-bell-group-count">
              {{ group.unreadCount }}
            </span>
            <button
              v-if="group.analysisId && group.unreadCount > 0"
              type="button"
              class="app-notification-bell-group-mark-read"
              :disabled="isAnalysisReadStateLoading(group.analysisId)"
              :title="$t('notifications-mark-all-read').toString()"
              :aria-label="$t('notifications-mark-all-read').toString()"
              @click.stop="onMarkAnalysisRead(group)"
            >
              <b-spinner v-if="isAnalysisReadStateLoading(group.analysisId)" small />
              <app-icon v-else icon="check-all" />
            </button>
          </div>

          <template v-if="!isNotificationGroupCollapsed(group.key)">
            <div
              v-for="notification in group.notifications"
              :key="notification.id"
              class="app-notification-bell-item"
              :class="{
                'app-notification-bell-item-unread': !notification.read,
                'app-notification-bell-item-mention': isMention(notification),
                [`app-notification-bell-item-${notificationTone(notification)}`]: isEvaluationMessage(notification),
              }"
            >
              <a
                class="app-notification-bell-item-main"
                :href="notificationHref(notification)"
                @click="onNotificationClick($event, notification)"
              >
                <div
                  class="app-notification-bell-item-icon"
                  :class="`app-notification-bell-item-icon-${notificationTone(notification)}`"
                >
                  <app-icon :icon="notificationIcon(notification)" />
                </div>
                <div class="app-notification-bell-item-content">
                  <div class="app-notification-bell-item-text">
                    <span v-if="evalBatchLabel(notification)" class="app-notification-bell-item-eb">
                      {{ evalBatchLabel(notification) }}
                    </span>
                    {{ notificationTitle(notification) }}
                  </div>
                  <div v-if="notification.note_excerpt" class="app-notification-bell-item-excerpt">
                    {{ notification.note_excerpt }}
                  </div>
                  <small class="app-notification-bell-item-time">{{ formatDateTime(notification.created_at) }}</small>
                </div>
                <span v-if="!notification.read" class="app-notification-bell-item-dot" aria-hidden="true"></span>
              </a>
              <button
                type="button"
                class="app-notification-bell-item-read-toggle"
                :disabled="isNotificationReadStateLoading(notification.id)"
                :title="$t(notification.read ? 'notification-mark-unread' : 'notification-mark-read').toString()"
                :aria-label="$t(notification.read ? 'notification-mark-unread' : 'notification-mark-read').toString()"
                @click.stop="onToggleNotificationRead($event, notification)"
              >
                <b-spinner v-if="isNotificationReadStateLoading(notification.id)" small />
                <app-icon v-else :icon="notification.read ? 'envelope' : 'envelope-open'" />
              </button>
              <!-- Outside the link: text inside an <a> can't be selected by dragging, and a click would open the notes. -->
              <div v-if="evaluationMessage(notification)" class="app-notification-bell-item-message">
                <pre class="app-notification-bell-item-message-text">{{ evaluationMessage(notification) }}</pre>
                <button
                  type="button"
                  class="app-notification-bell-item-message-copy"
                  :title="$t('notification-copy-message').toString()"
                  :aria-label="$t('notification-copy-message').toString()"
                  @click.stop="onCopyMessage(evaluationMessage(notification))"
                >
                  <app-icon icon="clipboard" />
                </button>
              </div>
            </div>
          </template>
        </section>
      </template>

      <button
        v-if="hasMoreNotifications && !loading"
        type="button"
        class="app-notification-bell-show-more"
        :disabled="loadingMore"
        @click="onShowMore"
      >
        <b-spinner v-if="loadingMore" small />
        <template v-else>
          {{ $t("show-more") }}
          <app-icon icon="chevron-down" />
        </template>
      </button>
    </div>
  </b-nav-item-dropdown>
</template>

<script lang="ts">
import { Component } from "vue-property-decorator";
import AppIcon from "@/app/shared/components/app-icon/app-icon.vue";
import { BaseAuthOnlineComponent } from "@/app/shared/components/base-auth-component/base-auth-online-component";
import { CatchError } from "@/app/shared/services/helper/catch-helper";
import type { NotificationSchema } from "@/app/shared/services/volateq-api/api-schemas/notification-schema";
import { AnalysisNotesHelper } from "@/app/shared/services/helper/analysis-notes-helper";
import { NotificationHelper } from "@/app/shared/services/helper/notification-helper";
import type { NotificationGroup } from "@/app/shared/services/helper/notification-helper";
import { NotificationChannel, NotificationPollUpdate } from "@/app/shared/services/realtime/channels/notification-channel";
import { notesModalService } from "@/app/shared/services/notes-modal-service";
import { OsNotificationHelper } from "@/app/shared/services/helper/os-notification-helper";
import { NotificationLeader } from "@/app/shared/services/helper/notification-leader";

const NOTIFICATIONS_PER_PAGE = 30;

@Component({
  name: "app-notification-bell",
  components: {
    AppIcon,
  },
})
export default class AppNotificationBell extends BaseAuthOnlineComponent {
  notifications: NotificationSchema[] = [];
  unreadCount = 0;
  loading = false;
  loadingMore = false;
  showReadNotifications = false;
  hasMoreNotifications = false;
  collapsedNotificationGroupKeys: string[] = [];
  /** Group order pinned while the panel stays open — see NotificationHelper.groupKeys. */
  notificationGroupKeyOrder: string[] = [];
  notificationReadStateLoadingIds: string[] = [];
  analysisReadStateLoadingIds: string[] = [];

  private unsubscribePoll: (() => void) | null = null;
  private notificationPage = 0;

  created() {
    this.unsubscribePoll = NotificationChannel.getInstance().subscribe(update => this.onPollUpdate(update));
  }

  beforeDestroy() {
    this.unsubscribePoll?.();
    this.unsubscribePoll = null;
  }

  get unreadCountLabel(): string {
    return this.unreadCount > 99 ? "99+" : String(this.unreadCount);
  }

  get notificationGroups(): NotificationGroup[] {
    return NotificationHelper.groupByAnalysis(
      this.notifications,
      this.$t("system").toString(),
      this.notificationGroupKeyOrder,
    );
  }

  @CatchError("loading")
  async onOpen() {
    // Opening the dropdown is a user gesture — the right moment to ask for OS
    // notification permission. Browsers ignore the request outside a gesture.
    void OsNotificationHelper.requestPermission();

    this.showReadNotifications = false;
    await this.loadNotifications();

    const { count } = await this.volateqApi.getUnreadNotificationCount();
    NotificationChannel.getInstance().setCount(count);
  }

  @CatchError("loading")
  async onShowUnread() {
    if (!this.showReadNotifications) {
      return;
    }
    this.showReadNotifications = false;
    await this.loadNotifications();
  }

  @CatchError("loading")
  async onShowAll() {
    if (this.showReadNotifications) {
      return;
    }
    this.showReadNotifications = true;
    await this.loadNotifications();
  }

  @CatchError("loadingMore")
  async onShowMore() {
    const nextPage = this.notificationPage + 1;
    const notifications = await this.volateqApi.getNotifications(
      !this.showReadNotifications,
      nextPage,
      NOTIFICATIONS_PER_PAGE,
      this.showReadNotifications,
    );
    this.notifications = [...this.notifications, ...notifications];
    this.notificationGroupKeyOrder = NotificationHelper.groupKeys(this.notifications);
    this.notificationPage = nextPage;
    this.hasMoreNotifications = notifications.length === NOTIFICATIONS_PER_PAGE;
  }

  @CatchError()
  onToggleNotificationGroup(groupKey: string) {
    this.collapsedNotificationGroupKeys = this.isNotificationGroupCollapsed(groupKey)
      ? this.collapsedNotificationGroupKeys.filter(key => key !== groupKey)
      : [...this.collapsedNotificationGroupKeys, groupKey];
  }

  isNotificationGroupCollapsed(groupKey: string): boolean {
    return this.collapsedNotificationGroupKeys.includes(groupKey);
  }

  @CatchError()
  async onToggleNotificationRead(event: Event, notification: NotificationSchema) {
    event.preventDefault();

    if (this.isNotificationReadStateLoading(notification.id)) {
      return;
    }

    this.notificationReadStateLoadingIds = [...this.notificationReadStateLoadingIds, notification.id];
    try {
      if (notification.read) {
        await this.volateqApi.markNotificationUnread(notification.id);
        NotificationChannel.getInstance().setCount(this.unreadCount + 1);
        this.notifications = this.notifications.filter(item => item.id !== notification.id);
        return;
      }

      await this.volateqApi.markNotificationRead(notification.id);
      NotificationChannel.getInstance().setCount(Math.max(0, this.unreadCount - 1));

      if (this.showReadNotifications) {
        notification.read = true;
      } else {
        this.notifications = this.notifications.filter(item => item.id !== notification.id);
      }
    } finally {
      this.notificationReadStateLoadingIds = this.notificationReadStateLoadingIds
        .filter(notificationId => notificationId !== notification.id);
    }
  }

  isNotificationReadStateLoading(notificationId: string): boolean {
    return this.notificationReadStateLoadingIds.includes(notificationId);
  }

  @CatchError()
  async onMarkAnalysisRead(group: NotificationGroup) {
    if (!group.analysisId || this.isAnalysisReadStateLoading(group.analysisId)) {
      return;
    }

    this.analysisReadStateLoadingIds = [...this.analysisReadStateLoadingIds, group.analysisId];
    try {
      await this.volateqApi.markAnalysisNotificationsRead(group.analysisId);
      const { count } = await this.volateqApi.getUnreadNotificationCount();
      NotificationChannel.getInstance().setCount(count);

      this.notifications = this.showReadNotifications
        ? this.notifications.map(notification => notification.analysis_id === group.analysisId
          ? { ...notification, read: true }
          : notification)
        : this.notifications.filter(notification => notification.analysis_id !== group.analysisId);
    } finally {
      this.analysisReadStateLoadingIds = this.analysisReadStateLoadingIds
        .filter(analysisId => analysisId !== group.analysisId);
    }
  }

  isAnalysisReadStateLoading(analysisId: string): boolean {
    return this.analysisReadStateLoadingIds.includes(analysisId);
  }

  notificationHref(notification: NotificationSchema): string | undefined {
    if (notification.analysis_id) {
      return `/analysis/${notification.analysis_id}`;
    }
    return undefined;
  }

  @CatchError()
  async onNotificationClick(event: Event, notification: NotificationSchema) {
    const mouseEvent = event as MouseEvent;
    const isModifierClick = mouseEvent.ctrlKey || mouseEvent.metaKey;

    if (!isModifierClick) {
      event.preventDefault();
    }

    if (!notification.read) {
      await this.volateqApi.markNotificationRead(notification.id);
      NotificationChannel.getInstance().setCount(Math.max(0, this.unreadCount - 1));

      if (this.showReadNotifications) {
        notification.read = true;
      } else {
        this.notifications = this.notifications.filter(item => item.id !== notification.id);
      }
    }

    if (isModifierClick || !notification.analysis_id) {
      return;
    }

    notesModalService.open(notification.analysis_id);
  }

  @CatchError()
  async onMarkAllRead() {
    if (!confirm(this.$t("apply-are-you-sure").toString())) {
      return;
    }

    await this.volateqApi.markAllNotificationsRead();
    this.notifications = this.showReadNotifications
      ? this.notifications.map(notification => ({ ...notification, read: true }))
      : [];
    if (!this.showReadNotifications) {
      this.hasMoreNotifications = false;
    }
    NotificationChannel.getInstance().setCount(0);
  }

  /** One-line summary for the toast and the OS popup; leads with the evaluation batch, if any. */
  notificationText(notification: NotificationSchema): string {
    const title = this.notificationTitle(notification);
    const evalBatch = NotificationHelper.evalBatchLabel(notification);
    return evalBatch ? `${evalBatch} · ${title}` : title;
  }

  notificationTitle(notification: NotificationSchema): string {
    const actor = notification.actor ? AnalysisNotesHelper.fullName(notification.actor) : this.$t("system").toString();
    const analysis = notification.analysis_name || "";

    if (notification.notification_type === "MENTION") {
      return analysis
        ? this.$t("notification-mention", { actor, analysis }).toString()
        : this.$t("notification-mention-no-analysis", { actor }).toString();
    }

    if (notification.notification_type === "ROLE_ASSIGNED") {
      const roleContext = NotificationHelper.parseRoleAssignedContext(notification);
      const role = roleContext.role ? this.$t("user-role-" + roleContext.role).toString() : "";
      if (roleContext.autoAssignReason) {
        return this.$t(
          "notification-role-auto-assigned-" + roleContext.autoAssignReason,
          { actor, role, analysis },
        ).toString();
      }
      return this.$t("notification-role-assigned", { actor, role, analysis }).toString();
    }

    if (notification.notification_type === "READY_FOR_REVIEW") {
      return this.$t("notification-ready-for-review", { actor, analysis }).toString();
    }

    if (notification.notification_type === "UPLOAD_COMPLETED") {
      const fh2ImportCounts = NotificationHelper.parseFh2ImportCounts(notification);
      if (fh2ImportCounts) {
        return this.$t("notification-fh2-import-completed", {
          analysis,
          succeeded: fh2ImportCounts.succeeded,
          failed: fh2ImportCounts.failed,
        }).toString();
      }
      return NotificationHelper.isIncompleteUpload(notification)
        ? this.$t("notification-upload-completed-data-incomplete", { analysis }).toString()
        : this.$t("notification-upload-completed", { analysis }).toString();
    }

    if (notification.notification_type === "UPLOAD_FAILED") {
      const fh2ImportCounts = NotificationHelper.parseFh2ImportCounts(notification);
      if (fh2ImportCounts) {
        return this.$t("notification-fh2-import-failed", {
          analysis,
          succeeded: fh2ImportCounts.succeeded,
          failed: fh2ImportCounts.failed,
        }).toString();
      }
      return this.$t("notification-upload-failed", { analysis }).toString();
    }

    if (notification.notification_type === "NOTE_ADDED") {
      return this.$t("notification-note-added", { actor, analysis }).toString();
    }

    if (notification.notification_type === "REACTION") {
      const emoji = notification.context || "";
      return analysis
        ? this.$t("notification-reaction", { actor, emoji, analysis }).toString()
        : this.$t("notification-reaction-no-analysis", { actor, emoji }).toString();
    }

    // The message itself is shown apart from the title (see evaluationMessage), so it can be copied.
    if (notification.notification_type === "EVALUATION_ERROR") {
      return analysis
        ? this.$t("notification-evaluation-error", { analysis }).toString()
        : this.$t("notification-evaluation-error-no-analysis").toString();
    }

    if (notification.notification_type === "EVALUATION_INFO") {
      return analysis
        ? this.$t("notification-evaluation-info", { analysis }).toString()
        : this.$t("notification-evaluation-info-no-analysis").toString();
    }

    if (notification.notification_type === "WHATSAPP_MESSAGE") {
      return analysis
        ? this.$t("notification-whatsapp-message", { analysis }).toString()
        : this.$t("notification-whatsapp-message-no-analysis").toString();
    }

    if (notification.notification_type === "READY_FOR_EVAL") {
      const evalBatch = notification.context || "";
      return evalBatch
        ? this.$t("notification-ready-for-eval-eb", { analysis, eval_batch: evalBatch }).toString()
        : this.$t("notification-ready-for-eval", { analysis }).toString();
    }

    if (notification.notification_type === "DEADLINE_CHANGED") {
      const evalBatch = notification.context || "";
      return evalBatch
        ? this.$t("notification-deadline-changed-eb", { actor, analysis, eval_batch: evalBatch }).toString()
        : this.$t("notification-deadline-changed", { actor, analysis }).toString();
    }

    return "";
  }

  formatDateTime(isoDate: string): string {
    return AnalysisNotesHelper.formatDateTime(isoDate);
  }

  notificationIcon(notification: NotificationSchema): string {
    return NotificationHelper.icon(notification);
  }

  notificationTone(notification: NotificationSchema): string {
    return NotificationHelper.tone(notification);
  }

  isMention(notification: NotificationSchema): boolean {
    return NotificationHelper.isMention(notification);
  }

  isEvaluationMessage(notification: NotificationSchema): boolean {
    return NotificationHelper.isEvaluationMessage(notification);
  }

  evaluationMessage(notification: NotificationSchema): string | null {
    return NotificationHelper.evaluationMessage(notification);
  }

  evalBatchLabel(notification: NotificationSchema): string | null {
    return NotificationHelper.evalBatchLabel(notification);
  }

  @CatchError()
  async onCopyMessage(message: string | null) {
    if (!message) {
      return;
    }
    await navigator.clipboard.writeText(message);
    this.showSuccess(this.$t("notification-message-copied").toString());
  }

  private async loadNotifications(): Promise<void> {
    this.notificationPage = 0;
    this.notifications = await this.volateqApi.getNotifications(
      !this.showReadNotifications,
      this.notificationPage,
      NOTIFICATIONS_PER_PAGE,
      this.showReadNotifications,
    );
    // Pinned once per load: while the panel is open, marking a notification read must not make
    // its analysis group jump to another position under the user's eyes.
    this.notificationGroupKeyOrder = NotificationHelper.groupKeys(this.notifications);
    this.hasMoreNotifications = this.notifications.length === NOTIFICATIONS_PER_PAGE;
  }

  private onPollUpdate(update: NotificationPollUpdate) {
    this.unreadCount = update.count;

    if (update.newNotifications.length > 0 && this.shouldPlayNotificationSound) {
      this.playNotificationSound();
    }

    for (const notification of [...update.newNotifications].reverse()) {
      this.notify(notification);
    }
  }

  // Suppress the in-app sound on mobile (where it is intrusive and OS chimes
  // already cover it) and whenever OS notifications are allowed — the OS popup
  // brings its own sound, so playing ours too would double up.
  private get shouldPlayNotificationSound(): boolean {
    return !this.isMobile && OsNotificationHelper.permission !== "granted";
  }

  private notify(notification: NotificationSchema) {
    const text = this.notificationText(notification);
    const evaluationMessage = NotificationHelper.evaluationMessage(notification);

    // Only the primary tab creates the OS popup — if every open tab did, the
    // browser would coalesce the same-tag notifications instead of alerting.
    const shownAsOsNotification = NotificationLeader.isPrimary && OsNotificationHelper.show(text, {
      body: notification.note_excerpt || evaluationMessage || undefined,
      tag: notification.id,
      onClick: notification.analysis_id
        ? () => notesModalService.open(notification.analysis_id!)
        : undefined,
    });

    // In-app fallback banner: only when OS notifications are not covering it —
    // either because permission is missing or because this primary tab failed
    // to create the popup.
    const osNotificationsCoverIt = OsNotificationHelper.permission === "granted"
      && (shownAsOsNotification || !NotificationLeader.isPrimary);

    if (shownAsOsNotification || osNotificationsCoverIt) {
      return;
    }

    if (evaluationMessage) {
      this.showEvaluationToast(notification, text, evaluationMessage);
    } else {
      this.showInfo(text);
    }
  }

  /**
   * Toast coloured by severity (red error, yellow info) that shows the full message with a copy
   * button. Errors stay until closed, so a stack trace can be read and copied.
   */
  private showEvaluationToast(notification: NotificationSchema, title: string, message: string) {
    const h = this.$createElement;
    const body = h("div", { class: "app-notification-bell-toast-message" }, [
      h("pre", { class: "app-notification-bell-toast-message-text" }, message),
      h(
        "button",
        {
          class: "app-notification-bell-toast-message-copy",
          attrs: { type: "button" },
          on: { click: () => this.onCopyMessage(message) },
        },
        [h(AppIcon, { props: { icon: "clipboard" } }), " ", this.$t("notification-copy-message").toString()],
      ),
    ]);

    this.$bvToast.toast([body], {
      title,
      variant: this.notificationTone(notification),
      headerClass: "base-toast-header",
      bodyClass: "app-notification-bell-toast-body",
      toaster: "b-toaster-bottom-center",
      solid: true,
      noAutoHide: notification.notification_type === "EVALUATION_ERROR",
    });
  }

  private playNotificationSound() {
    try {
      const audioWindow = window as Window & { webkitAudioContext?: typeof AudioContext };
      const AudioContextClass = window.AudioContext || audioWindow.webkitAudioContext;
      if (!AudioContextClass) {
        return;
      }
      const ctx = new AudioContextClass();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.setValueAtTime(1047, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      oscillator.onended = () => {
        void ctx.close?.().catch(() => undefined);
      };
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch {
      // Audio not available — ignore silently.
    }
  }
}
</script>

<style lang="scss">
@import "@/scss/_colors.scss";

.app-notification-bell {
  .nav-link {
    overflow: visible;
  }

  &-toggle {
    overflow: visible;
    margin-right: 1em;
  }

  &-icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    vertical-align: middle;
    color: inherit;
  }

  &-badge {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(35%, -35%);
    min-width: 1rem;
    height: 1rem;
    padding: 0 0.15rem;
    border-radius: 50%;
    background-color: var(--danger-btn-bg-color);
    color: var(--danger-btn-text-color);
    border: 2px solid var(--muted-bg-color);
    box-sizing: border-box;
    font-size: 0.6rem;
    font-weight: 700;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  &-menu {
    width: 520px;
    max-width: calc(100vw - 1rem);
    padding: 0;
    overflow: hidden;
    border: 1px solid var(--default-border-color);
    border-radius: 0.75rem;
    background-color: var(--muted-bg-color);
    margin-right: 1em;
    text-align: left;
  }

  &-panel-header {
    padding: 1rem 1rem 0.85rem;
    border-bottom: 1px solid var(--default-border-color);
    background-color: var(--default-bg-color);
  }

  &-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
  }

  &-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    color: var(--default-text-color);
    font-size: 1.1rem;
    font-weight: 700;
  }

  &-count,
  &-group-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.5rem;
    height: 1.5rem;
    padding: 0 0.4rem;
    border-radius: 999px;
    background-color: var(--primary-bg-color);
    color: var(--primary-text-color);
    font-size: 0.7rem;
    font-weight: 700;
  }

  &-mark-all {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.25rem 0;
    border: 0;
    background: transparent;
    color: var(--action-text-color);
    white-space: nowrap;
    font-size: 0.8rem;
    cursor: pointer;

    &:hover,
    &:focus {
      color: var(--action-text-hover-color);
      text-decoration: underline;
    }
  }

  &-filter {
    display: inline-flex;
    gap: 0.2rem;
    margin-top: 0.85rem;
    padding: 0.2rem;
    border-radius: 0.5rem;
    background-color: var(--muted-bg-color);
  }

  &-filter-button {
    padding: 0.35rem 0.7rem;
    border: 0;
    border-radius: 0.35rem;
    background: transparent;
    color: var(--muted-text-color);
    font-size: 0.78rem;
    cursor: pointer;
    transition: background-color 120ms ease, color 120ms ease, box-shadow 120ms ease;

    &:hover,
    &:focus {
      color: var(--default-text-color);
    }

    &-active {
      background-color: var(--default-bg-color);
      color: var(--default-text-color);
      box-shadow: 0 1px 4px var(--default-shadow-color);
      font-weight: 600;
    }
  }

  &-list {
    max-height: 70vh;
    padding: 0.75rem;
    overflow: auto;
  }

  &-loading,
  &-empty {
    min-height: 10rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--muted-text-color);
  }

  &-empty {
    flex-direction: column;
    gap: 0.65rem;
    text-align: center;
  }

  &-empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background-color: var(--default-bg-color);
    color: var(--muted-text-color);
    font-size: 1.35rem;
  }

  &-group {
    overflow: hidden;
    border: 1px solid var(--default-border-color);
    border-radius: 0.6rem;
    background-color: var(--default-bg-color);

    & + & {
      margin-top: 0.75rem;
    }
  }

  &-group-header {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 2.45rem;
    border-bottom: 1px solid var(--default-border-color);
    background-color: var(--muted-bg-color);

    &-collapsed {
      border-bottom: 0;
    }
  }

  &-group-toggle {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    align-items: center;
    gap: 0.5rem;
    align-self: stretch;
    padding: 0.55rem 0.75rem;
    border: 0;
    background: transparent;
    color: var(--default-text-color);
    text-align: left;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: var(--secondary-bg-highlight-color);
    }
  }

  &-group-icon {
    flex: 0 0 auto;
    color: var(--muted-text-color);
  }

  &-group-name {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.83rem;
    font-weight: 700;
  }

  &-group-count {
    min-width: 1.25rem;
    height: 1.25rem;
    font-size: 0.65rem;
  }

  &-group-toggle-icon {
    flex: 0 0 auto;
    color: var(--muted-text-color);
    font-size: 0.75rem;
  }

  &-group-mark-read {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    margin: 0 0.35rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--muted-text-color);
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: var(--secondary-bg-highlight-color);
      color: var(--action-text-color);
    }

    &:disabled {
      cursor: wait;
    }
  }

  &-show-more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    width: 100%;
    min-height: 2.6rem;
    margin-top: 0.75rem;
    border: 1px solid var(--default-border-color);
    border-radius: 0.6rem;
    background-color: var(--default-bg-color);
    color: var(--action-text-color);
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: var(--secondary-bg-highlight-color);
      color: var(--action-text-hover-color);
    }

    &:disabled {
      color: var(--muted-text-color);
      cursor: wait;
    }
  }

  &-item {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    border-bottom: 1px solid var(--default-border-color);

    &:last-child {
      border-bottom: 0;
    }

    &-main {
      position: relative;
      display: flex;
      min-width: 0;
      // Zero basis: with the row wrapping for the message block, an "auto" basis (the full text
      // width) would push the read toggle onto its own line.
      flex: 1 1 0;
      align-items: flex-start;
      gap: 0.7rem;
      padding: 0.75rem;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
      color: var(--default-text-color);
      text-decoration: none;
      transition: background-color 120ms ease;

      &:hover,
      &:focus,
      &:active {
        color: var(--default-text-color);
        background-color: var(--secondary-bg-highlight-color);
      }
    }

    &-unread {
      background-color: var(--secondary-bg-highlight-color);
    }

    // A mention is addressed at the user personally, so it has to stand out from the analysis
    // noise around it - read or unread. Listed after -unread so it wins over that background.
    // The accent bar is an inset shadow, so mention rows stay aligned with the others.
    &-mention {
      box-shadow: inset 3px 0 0 var(--mention-bg-color);
      background-color: var(--mention-highlight-bg-color);
    }

    // Evaluation messages: the accent bar tells errors (red) from infos (yellow) at a glance.
    &-danger {
      box-shadow: inset 3px 0 0 var(--danger-bg-color);
    }

    &-warning {
      box-shadow: inset 3px 0 0 var(--warning-bg-color);
    }

    &-eb {
      display: inline-block;
      margin-right: 0.3rem;
      padding: 0 0.35rem;
      background-color: var(--primary-bg-color);
      color: var(--primary-text-color);
      font-size: 0.75rem;
      font-weight: 700;
      line-height: 1.4;
      vertical-align: text-bottom;
    }

    // Indented to the text column: item padding + icon width + gap.
    &-message {
      display: flex;
      flex: 1 0 100%;
      align-items: flex-start;
      gap: 0.35rem;
      padding: 0 0.75rem 0.75rem 3.45rem;
      min-width: 0;

      &-text {
        flex: 1 1 auto;
        min-width: 0;
        max-height: 12rem;
        margin: 0;
        padding: 0.4rem 0.5rem;
        overflow: auto;
        border: 1px solid var(--default-border-color);
        background-color: var(--default-bg-color);
        color: var(--default-text-color);
        font-size: 0.72rem;
        line-height: 1.35;
        white-space: pre-wrap;
        word-break: break-word;
        user-select: text;
        cursor: text;
      }

      &-copy {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: transparent;
        color: var(--muted-text-color);
        cursor: pointer;

        &:hover,
        &:focus {
          background-color: var(--secondary-bg-highlight-color);
          color: var(--action-text-color);
        }
      }
    }

    &-icon {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      margin-top: 0.05rem;
      border-radius: 0.5rem;
      background-color: var(--secondary-bg-highlight-color);
      color: var(--primary-bg-color);

      &-success {
        background-color: var(--success-bg-color);
        color: var(--success-text-color);
      }

      &-warning {
        background-color: var(--warning-bg-color);
        color: var(--warning-text-color);
      }

      &-danger {
        background-color: var(--danger-bg-color);
        color: var(--danger-text-color);
      }

      &-mention {
        background-color: var(--mention-bg-color);
        color: var(--mention-text-color);
      }
    }

    &-content {
      min-width: 0;
      flex: 1 1 auto;
    }

    &-text {
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
      color: var(--default-text-color);
      font-size: 0.86rem;
      line-height: 1.35;
    }

    &-unread &-text {
      font-weight: 600;
    }

    &-excerpt {
      display: -webkit-box;
      margin-top: 0.3rem;
      overflow: hidden;
      color: var(--muted-text-color);
      font-size: 0.78rem;
      line-height: 1.35;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
    }

    &-time {
      display: block;
      margin-top: 0.35rem;
      color: var(--muted-text-color);
      font-size: 0.7rem;
    }

    &-dot {
      flex: 0 0 auto;
      width: 0.45rem;
      height: 0.45rem;
      margin-top: 0.75rem;
      border-radius: 50%;
      background-color: var(--primary-bg-color);
    }

    &-read-toggle {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      align-self: center;
      width: 2.25rem;
      height: 2.25rem;
      margin-right: 0.35rem;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: transparent;
      color: var(--muted-text-color);
      font-size: 0.85rem;
      cursor: pointer;

      &:hover,
      &:focus {
        background-color: var(--secondary-bg-highlight-color);
        color: var(--action-text-color);
      }

      &:disabled {
        color: var(--muted-text-color);
        cursor: wait;
      }
    }
  }
}

// Match other header utility icons (gear, theme, locale) instead of navbar-dark link color.
.app-header .app-notification-bell {
  &.show > .nav-link.app-notification-bell-toggle {
    color: var(--secondary-text-color);
  }

  .nav-link.app-notification-bell-toggle {
    color: var(--muted-text-color);

    &:hover,
    &:focus {
      color: var(--secondary-text-color);
    }
  }
}

// Override app-header's right-aligned dropdown items for this menu only.
.app-header .dropdown .app-notification-bell-menu {
  .dropdown-item,
  .b-dropdown-text,
  .dropdown-header {
    text-align: left;
  }

  .app-notification-bell-item-text {
    color: var(--default-text-color);
  }

  .app-notification-bell-item-unread .app-notification-bell-item-text {
    color: var(--primary-bg-color);
  }
}

// The toast is rendered in the toaster, outside this component, hence top level.
.b-toaster .toast .app-notification-bell-toast-body {
  padding: 0.5em 1em 0.75em;
}

.app-notification-bell-toast-message {
  &-text {
    max-height: 40vh;
    margin: 0 0 0.5rem;
    overflow: auto;
    color: inherit;
    font-size: 0.75rem;
    white-space: pre-wrap;
    word-break: break-word;
    user-select: text;
  }

  &-copy {
    padding: 0.2rem 0.6rem;
    border: 1px solid currentColor;
    border-radius: 0.25rem;
    background: transparent;
    color: inherit;
    font-size: 0.8rem;
    cursor: pointer;
  }
}
</style>
